import React, { useState } from 'react';
import { FileDown, Building2, Calendar, Users, FileText } from 'lucide-react';
import DateInput from './DateInput';
import './AppointmentOrderForm.css';

const mockFormData = {
  establishmentName: "ABC Manufacturing Pvt Ltd",
  establishmentCode: "KRBNG12345678",
  address: "Plot 45, Industrial Area, Whitefield, Bangalore - 560066",
  month: "07",
  year: "2025",
  wageMonth: "JUL 2025",

  employees: [
    {
      uan: "101234567890",
      name: "RAJESH KUMAR",
      grossWages: "48000",
      epfWages: "15000",
      epsWages: "15000",
      edliWages: "15000",
      eeShare: "1800",
      erShare: "1800",
      epsContribution: "1250",
      ncp: "0"
    },
    {
      uan: "101234567891",
      name: "ANITA SHARMA",
      grossWages: "42000",
      epfWages: "15000",
      epsWages: "15000",
      edliWages: "15000",
      eeShare: "1800",
      erShare: "1800",
      epsContribution: "1250",
      ncp: "0"
    },
    {
      uan: "101234567892",
      name: "SURESH PATEL",
      grossWages: "38000",
      epfWages: "15000",
      epsWages: "15000",
      edliWages: "15000",
      eeShare: "1800",
      erShare: "1800",
      epsContribution: "1250",
      ncp: "0"
    }
  ],

  totalEEShare: "5400",
  totalERShare: "5400",
  totalEPSContribution: "3750",
  preparedBy: "HR Manager",
  place: "Bangalore",
  date: "2025-07-31"
};

const ECRGenerator = () => {
  const [formData, setFormData] = useState(mockFormData);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEmployeeChange = (index, field, value) => {
    const updatedEmployees = [...formData.employees];
    updatedEmployees[index] = { ...updatedEmployees[index], [field]: value };
    setFormData(prev => ({ ...prev, employees: updatedEmployees }));
  };

  const generateECRFile = () => {
    setIsGenerating(true);

    try {
      let ecrContent = '';

      // Header Section
      ecrContent += '#1\n';
      ecrContent += `ESTABLISHMENT CODE|${formData.establishmentCode}\n`;
      ecrContent += `ESTABLISHMENT NAME|${formData.establishmentName}\n`;
      ecrContent += `WAGE MONTH|${formData.wageMonth}\n`;
      ecrContent += `ADDRESS|${formData.address}\n`;
      ecrContent += '\n';

      // Employee Records Header
      ecrContent += '#2\n';
      ecrContent += 'UAN|MEMBER NAME|GROSS WAGES|EPF WAGES|EPS WAGES|EDLI WAGES|EE SHARE|EPS CONTRIBUTION|ER SHARE|NCP DAYS\n';

      // Employee Records
      formData.employees.forEach(emp => {
        ecrContent += `${emp.uan}|${emp.name}|${emp.grossWages}|${emp.epfWages}|${emp.epsWages}|${emp.edliWages}|${emp.eeShare}|${emp.epsContribution}|${emp.erShare}|${emp.ncp}\n`;
      });

      ecrContent += '\n';

      // Footer/Summary Section
      ecrContent += '#3\n';
      ecrContent += `TOTAL EMPLOYEES|${formData.employees.length}\n`;
      ecrContent += `TOTAL EE SHARE|${formData.totalEEShare}\n`;
      ecrContent += `TOTAL ER SHARE|${formData.totalERShare}\n`;
      ecrContent += `TOTAL EPS CONTRIBUTION|${formData.totalEPSContribution}\n`;
      ecrContent += '\n';

      // Metadata
      ecrContent += '#4\n';
      ecrContent += `PREPARED BY|${formData.preparedBy}\n`;
      ecrContent += `PLACE|${formData.place}\n`;
      ecrContent += `DATE|${formData.date}\n`;
      ecrContent += `GENERATED ON|${new Date().toLocaleString()}\n`;
      ecrContent += 'GENERATED BY|PayrollGen - ECR Generator\n';

      // Create blob and download
      const blob = new Blob([ecrContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ECR-${formData.month}${formData.year}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setTimeout(() => {
        setIsGenerating(false);
        alert('ECR text file generated successfully!');
      }, 500);
    } catch (error) {
      console.error('Error generating ECR file:', error);
      alert('Error generating ECR file. Please try again.');
      setIsGenerating(false);
    }
  };

  return (
    <div className="appointment-order-container">
      <div className="appointment-header">
        <div>
          <h2 className="appointment-title">ECR - Electronic Challan-cum-Return</h2>
          <p className="appointment-subtitle">EPFO Monthly Return Text File Generator</p>
        </div>
      </div>

      <div className="appointment-content">
        <div className="info-alert">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <span>Review ECR details below. This will generate a text file for EPFO submission.</span>
        </div>

        <div className="form-section">
          <div className="section-header">
            <Building2 size={20} />
            <h3>Organization Details</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Organization Name</label>
              <input type="text" value={formData.establishmentName} onChange={(e) => handleInputChange('establishmentName', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Organization Code</label>
              <input type="text" value={formData.establishmentCode} onChange={(e) => handleInputChange('establishmentCode', e.target.value)} />
            </div>
            <div className="form-field full-width">
              <label>Address</label>
              <textarea value={formData.address} onChange={(e) => handleInputChange('address', e.target.value)} rows={2} />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <Calendar size={20} />
            <h3>Wage Period</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Month (MM)</label>
              <select value={formData.month} onChange={(e) => handleInputChange('month', e.target.value)}>
                {[
                  { value: '01', label: 'January' },
                  { value: '02', label: 'February' },
                  { value: '03', label: 'March' },
                  { value: '04', label: 'April' },
                  { value: '05', label: 'May' },
                  { value: '06', label: 'June' },
                  { value: '07', label: 'July' },
                  { value: '08', label: 'August' },
                  { value: '09', label: 'September' },
                  { value: '10', label: 'October' },
                  { value: '11', label: 'November' },
                  { value: '12', label: 'December' }
                ].map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Year</label>
              <input type="number" value={formData.year} onChange={(e) => handleInputChange('year', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Wage Month Display</label>
              <input type="text" value={formData.wageMonth} onChange={(e) => handleInputChange('wageMonth', e.target.value)} placeholder="e.g., JUL 2025" />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <Users size={20} />
            <h3>Employee Contribution Details</h3>
          </div>

          {formData.employees.map((emp, idx) => (
            <div key={idx} style={{ background: 'var(--theme-surface)', padding: '1.5rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid #e5e7eb' }}>
              <div className="form-grid">
                <div className="form-field">
                  <label>UAN</label>
                  <input type="text" value={emp.uan} onChange={(e) => handleEmployeeChange(idx, 'uan', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Member Name (UPPERCASE)</label>
                  <input type="text" value={emp.name} onChange={(e) => handleEmployeeChange(idx, 'name', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>Gross Wages (₹)</label>
                  <input type="number" value={emp.grossWages} onChange={(e) => handleEmployeeChange(idx, 'grossWages', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>EPF Wages (₹)</label>
                  <input type="number" value={emp.epfWages} onChange={(e) => handleEmployeeChange(idx, 'epfWages', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>EPS Wages (₹)</label>
                  <input type="number" value={emp.epsWages} onChange={(e) => handleEmployeeChange(idx, 'epsWages', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>EDLI Wages (₹)</label>
                  <input type="number" value={emp.edliWages} onChange={(e) => handleEmployeeChange(idx, 'edliWages', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>EE Share (₹)</label>
                  <input type="number" value={emp.eeShare} onChange={(e) => handleEmployeeChange(idx, 'eeShare', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>EPS Contribution (₹)</label>
                  <input type="number" value={emp.epsContribution} onChange={(e) => handleEmployeeChange(idx, 'epsContribution', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>ER Share (₹)</label>
                  <input type="number" value={emp.erShare} onChange={(e) => handleEmployeeChange(idx, 'erShare', e.target.value)} />
                </div>
                <div className="form-field">
                  <label>NCP Days</label>
                  <input type="number" value={emp.ncp} onChange={(e) => handleEmployeeChange(idx, 'ncp', e.target.value)} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="form-section">
          <div className="section-header">
            <FileText size={20} />
            <h3>Summary & Metadata</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Total EE Share (₹)</label>
              <input type="number" value={formData.totalEEShare} onChange={(e) => handleInputChange('totalEEShare', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Total ER Share (₹)</label>
              <input type="number" value={formData.totalERShare} onChange={(e) => handleInputChange('totalERShare', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Total EPS Contribution (₹)</label>
              <input type="number" value={formData.totalEPSContribution} onChange={(e) => handleInputChange('totalEPSContribution', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Prepared By</label>
              <input type="text" value={formData.preparedBy} onChange={(e) => handleInputChange('preparedBy', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Place</label>
              <input type="text" value={formData.place} onChange={(e) => handleInputChange('place', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Date</label>
              <DateInput value={formData.date} onChange={(e) => handleInputChange('date', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button onClick={generateECRFile} disabled={isGenerating} className={`btn-generate ${isGenerating ? 'generating' : ''}`}>
            {isGenerating ? (
              <>
                <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" opacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 0 20" opacity="0.75"/>
                </svg>
                <span>Generating ECR File...</span>
              </>
            ) : (
              <>
                <FileDown size={20} />
                <span>Generate & Download ECR Text File</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ECRGenerator;
